import {
    PRODUCT_CLASS_CAMPAIGN,
    PRODUCT_CLASS_LANGUAGE_LEARNING,
    PRODUCT_CLASS_LOCAL,
    PRODUCT_CLASS_LOTE,
    PRODUCT_CLASS_STANDARD
} from '../../store/constants';

import FormatUtils from './utils-format';
import RoutingUtils from './utils-routing';
import { ProductMetaData } from '../../../models/Product';
import { ENVIRONMENT_DEMO, ENVIRONMENT_PRODUCTION, ENVIRONMENT_STAGE } from '../../constants';
import { To } from 'react-router-dom';
import { ProductFilter } from '../../store/api';

export const CATEGORY_TYPE_PUBLISHER = 'publishers';

interface CreateMetaDataLinkParams {
    productClass: string | undefined;
    filter: ProductFilter;
    extra: {
        readonly loanFormatKey?: string | null;
        readonly language?: string | null;
        readonly categoryType?: string | null;
        readonly publisher?: string | null;
    };
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    ageCategory: string | undefined;
}

export default class MetadataUtils {
    static createMetaDataLink({
        extra = {},
        productClass,
        filter,
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    }: CreateMetaDataLinkParams): To {
        const { loanFormatKey, language, categoryType } = extra;

        if (productClass === PRODUCT_CLASS_LOCAL) {
            return RoutingUtils.libxLocationDescriptorString({
                filter,
                isMultiLanguageShopEnabled,
                shopLanguage,
                ageCategory
            });
        }

        if (productClass === PRODUCT_CLASS_LANGUAGE_LEARNING) {
            return RoutingUtils.languageLearningDescriptorString({
                language,
                loanFormat: loanFormatKey,
                filter,
                shopLanguage,
                isMultiLanguageShopEnabled,
                ageCategory
            });
        }

        const isForeignLanguageProduct = productClass === PRODUCT_CLASS_LOTE;

        if (isForeignLanguageProduct && !isMultiLanguageShopEnabled) {
            return RoutingUtils.foreignLanguageDescriptorString({
                language,
                loanFormat: loanFormatKey,
                filter,
                ageCategory
            });
        }

        if (productClass === PRODUCT_CLASS_STANDARD || (isMultiLanguageShopEnabled && isForeignLanguageProduct)) {
            if (categoryType === CATEGORY_TYPE_PUBLISHER) {
                const { publisher } = extra;
                return RoutingUtils.publisherPageLocationDescriptor({
                    loanFormat: loanFormatKey,
                    publisher,
                    isMultiLanguageShopEnabled,
                    shopLanguage,
                    ageCategory
                });
            } else {
                return RoutingUtils.catalogueLocationDescriptorString({
                    loanFormat: loanFormatKey,
                    filter,
                    shopLanguage,
                    isMultiLanguageShopEnabled,
                    ageCategory
                });
            }
        }

        if (productClass === PRODUCT_CLASS_CAMPAIGN) {
            return RoutingUtils.campaignsLocationDescriptorString({
                loanFormat: loanFormatKey,
                filter,
                isMultiLanguageShopEnabled,
                shopLanguage,
                ageCategory
            });
        }

        return '';
    }

    static getPerformerFullname(performer: {
        readonly firstName?: string;
        readonly lastName?: string;
        readonly name?: string;
    }): string | undefined {
        return `${performer.firstName || ''} ${performer.lastName || ''}`.trim() || performer.name;
    }

    static performerPhotoUrl(
        performerId: string,
        env: typeof ENVIRONMENT_DEMO | typeof ENVIRONMENT_STAGE | typeof ENVIRONMENT_PRODUCTION
    ): string {
        const numericId = performerId.split('@@')[0];
        const host =
            env === ENVIRONMENT_PRODUCTION || env === ENVIRONMENT_DEMO ? 'static.borrowbox.com' : 'static.borrowbox.io';
        return `//${host}/shared/performerPhoto/${numericId}.jpg`;
    }

    static withdrawnProductLine(product: {
        readonly id: string;
        readonly title?: string;
        readonly nameOfFirstAuthor?: string;
        readonly isbn13?: string;
    }): string {
        if (product.title) {
            return `${FormatUtils.truncate(product.nameOfFirstAuthor)} - ${product.title} (${product.isbn13})`;
        }
        // product.id is now a line item key, which has the form BOL_000042-null-null-null for unavailable line items
        // since it is directly presented to the user,the -null suffixes  need to be removed
        return product.id?.replace(/-null/gi, '');
    }

    static isProductAvailableForSale(productOrPrice: { readonly netValue?: number } | undefined | null): boolean {
        if (!productOrPrice) {
            return false;
        }

        if (typeof productOrPrice.netValue !== 'number') {
            return false;
        }

        return productOrPrice.netValue > 0;
    }

    static isProductPreOrderable(product: ProductMetaData): boolean {
        if (!product.salesStatus) {
            return false;
        }
        return product.salesStatus.key === 'PREORDER' || product.salesStatus.key === 'PREORDER_EMBARGO';
    }
}
